/* eslint-disable import/no-cycle */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { removeAuthToken, selectIsAuthenticated, setAuthToken } from './authTokenSlice';
import { getResyConfigThunk } from './resyConfigSlice';

import {
  getAuthDestroy,
  getUser,
  postAuthEmailRequest,
  postAuthEmailVerify,
  postAuthChallenge,
  postAuthMobile,
  postAuthPassword,
  postRegisterUser,
  postUserLocation,
} from '../api';
import {
  FAILED, IDLE, PENDING, SUCCEEDED,
} from '../constants/asyncLoadingStatuses';
import { isValidLatLong, userHasAmexCard } from '../utils/userSliceUtils';
import {
  clearReservationsChangedAt,
  getReservationsChangedAt,
  setReservationsChangedAt,
} from '../utils/reservationsChangedStorage';
import { getListVenuesByUserThunk } from './favoriteVenuesSlice';
import { fetchExperiments, resetExperiments } from './experimentsSlice';
import { resetAmexAds } from './amexAdsSlice';

// The backend can take a while to reflect a reservation change in /2/user
// (e.g. num_bookings). A /2/user fetch made within this window after a change
// on a screen that opts in gets a final follow-up fetch once the window has passed.
const RESERVATIONS_CHANGE_SETTLE_MS = 30 * 1000;
// If more than this much of the window is left when that fetch lands, also do an
// early follow-up halfway to the final one, since the backend often catches up sooner.
const EARLY_FOLLOW_UP_THRESHOLD_MS = RESERVATIONS_CHANGE_SETTLE_MS / 2;

// /2/user fetch bookkeeping. Module-level because the store is a singleton
// and none of this belongs in serializable state.
// Every /2/user request records when it was issued; if requests overlap, the one issued last wins.
let lastAppliedRequestIssuedAt = 0;
// Bumped whenever the logged-in session changes (login, logout). A /2/user request carries the
// generation it started in, so a request from an earlier session never applies its data to the new
// one and never logs it out. Token refreshes don't change the session.
let userSessionGeneration = 0;
let earlyFollowUpTimer = null;
let followUpTimer = null;
let followUpAt = 0; // when the final follow-up is due

// Call when the logged-in session changes
const resetUserFetchState = () => {
  userSessionGeneration += 1;
  clearTimeout(earlyFollowUpTimer);
  clearTimeout(followUpTimer);
  lastAppliedRequestIssuedAt = 0;
  earlyFollowUpTimer = null;
  followUpTimer = null;
  followUpAt = 0;
};

const postRegisterUserThunk = createAsyncThunk(
  'user/registerUser',
  async ({ userObj, captchaResponse }, { rejectWithValue }) => {
    try {
      const response = await postRegisterUser(userObj, captchaResponse);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const loadUserThunk = createAsyncThunk(
  'user/getUser',
  async ({ logoutOnError = true, sessionGeneration } = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await getUser();
      dispatch(getListVenuesByUserThunk());
      return response;
    } catch (error) {
      // Don't let a failed request from an earlier session log out the current one
      const isCurrentSession = sessionGeneration === undefined || sessionGeneration === userSessionGeneration;
      if (logoutOnError && isCurrentSession) {
        dispatch(userLogoutThunk());
      }
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthEmailRequestThunk = createAsyncThunk(
  'user/requestEmailCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthEmailRequest(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthEmailVerifyThunk = createAsyncThunk(
  'user/verifyEmailCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthEmailVerify(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthPasswordThunk = createAsyncThunk(
  'user/authenticatePassword',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthPassword(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthMobileThunk = createAsyncThunk(
  'user/authenticateMobile',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthMobile(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthChallengeThunk = createAsyncThunk(
  'user/authenticateChallenge',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthChallenge(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    registrationError: null,
    registrationStatus: IDLE,
    reservationToBeModified: undefined,
    shouldUserReservationsUpdate: null,
    userData: null,
    userLocation: {},
  },
  reducers: {
    setReservationToBeModified: (state, action) => {
      const reservation = action.payload;
      state.reservationToBeModified = reservation;
    },
    setShouldUserReservationsUpdate: (state, action) => {
      const params = action.payload;
      state.shouldUserReservationsUpdate = params;
    },
    setUserData: (state, action) => {
      const data = action.payload;
      state.userData = data;
      state.registrationError = null;
    },
    setUserLocation: (state, action) => {
      const location = action.payload;
      state.userLocation = location;
    },
    setUserLocationLatLong: (state, action) => {
      const { lat, long } = action.payload;
      if (isValidLatLong({ lat, long })) {
        state.userLocation.lat = lat;
        state.userLocation.long = long;
      }
    },
    resetRegistration: (state) => {
      state.registrationError = null;
      state.registrationStatus = IDLE;
    },
    resetReservationToBeModified: (state) => {
      state.reservationToBeModified = undefined;
    },
    resetShouldUserReservationsUpdate: (state) => {
      state.shouldUserReservationsUpdate = null;
    },
    resetUserData: (state) => {
      state.userData = null;
      state.registrationError = null;
    },
    setUpdatedPhoneNumber: (state, action) => {
      if (action.payload) {
        state.userData.mobile_number = (action.payload[0] === '+') ? action.payload : `+${action.payload}`;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postRegisterUserThunk.pending, (state) => {
      state.registrationStatus = PENDING;
    });
    builder.addCase(postRegisterUserThunk.fulfilled, (state) => {
      state.registrationStatus = SUCCEEDED;
    });
    builder.addCase(postRegisterUserThunk.rejected, (state, action) => {
      const errorData = action.payload.data.data;
      state.registrationError = errorData;
      state.registrationStatus = FAILED;
    });
  },
});

const {
  resetRegistration,
  resetReservationToBeModified,
  resetShouldUserReservationsUpdate,
  setReservationToBeModified,
  setShouldUserReservationsUpdate,
  setUserData,
  setUserLocation,
  setUserLocationLatLong,
  resetUserData,
  setUpdatedPhoneNumber,
} = userSlice.actions;

// Convenience getter-thunks for Angular controllers only, will be torn down in time
const getReservationToBeModifiedThunk = () => (dispatch, getState) => {
  const { user } = getState();
  return user.reservationToBeModified;
};

// Thunks
// Using function declarations so that names are hoisted,
// thus eliminating the need to juggle ordering to manage dependencies

// Options:
// - logoutOnError: false where a failed fetch should only show an error, not end the session
// - scheduleFollowUp: true on screens that show reservation-derived data (e.g. num_bookings), so a
//   fetch within the settle window after a reservation change gets one follow-up fetch afterwards
function getUserThunk(shouldRefresh = false, { logoutOnError = true, scheduleFollowUp = false } = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const { user: { userData } } = state;
    // Data fetched before the last reservation change is stale even if a caller didn't ask to refresh
    const isStaleFromReservationsChange = getReservationsChangedAt(userData?.id) > lastAppliedRequestIssuedAt;
    if ((userData === null || shouldRefresh || isStaleFromReservationsChange)) {
      if (selectIsAuthenticated(state)) {
        return dispatch(requestUserThunk({ logoutOnError, scheduleFollowUp }));
      }
    }
    return Promise.resolve(userData);
  };
}

function requestUserThunk({ logoutOnError = true, scheduleFollowUp = false } = {}) {
  return (dispatch, getState) => {
    const issuedAt = Date.now();
    const sessionGeneration = userSessionGeneration;
    return dispatch(getResyConfigThunk())
      .then(() => dispatch(loadUserThunk({ logoutOnError, sessionGeneration })).unwrap())
      .then((data) => {
        // Drop data from an earlier session
        if (sessionGeneration !== userSessionGeneration) {
          return getState().user.userData;
        }
        // Drop data from a request issued before one already applied, but still honor this caller's
        // follow-ups; the newer request may not have asked for them
        if (issuedAt < lastAppliedRequestIssuedAt) {
          if (scheduleFollowUp) {
            dispatch(scheduleUserFollowUpThunk());
          }
          return getState().user.userData;
        }
        lastAppliedRequestIssuedAt = issuedAt;
        dispatch(setUserData(data));
        if (scheduleFollowUp) {
          dispatch(scheduleUserFollowUpThunk());
        }
        return data;
      });
  };
}

function scheduleUserFollowUpThunk() {
  return (dispatch, getState) => {
    const changedAt = getReservationsChangedAt(getState().user.userData?.id);
    const settledAt = changedAt + RESERVATIONS_CHANGE_SETTLE_MS;
    if (!changedAt || lastAppliedRequestIssuedAt >= settledAt || followUpAt >= settledAt) {
      return;
    }

    const followUp = () => {
      if (selectIsAuthenticated(getState())) {
        // A failed background refresh shouldn't log the user out. scheduleFollowUp re-checks the
        // stored change time when this lands, so a newer change (e.g. from another tab) extends the
        // follow-ups to its own deadline; with no newer change, the settle-window checks stop it.
        dispatch(requestUserThunk({ logoutOnError: false, scheduleFollowUp: true })).catch(() => {});
      }
    };

    clearTimeout(earlyFollowUpTimer);
    clearTimeout(followUpTimer);
    earlyFollowUpTimer = null;
    followUpAt = settledAt;

    const timeToFinalFollowUp = settledAt - Date.now();
    if (timeToFinalFollowUp > EARLY_FOLLOW_UP_THRESHOLD_MS) {
      earlyFollowUpTimer = setTimeout(() => {
        earlyFollowUpTimer = null;
        followUp();
      }, timeToFinalFollowUp / 2);
    }
    followUpTimer = setTimeout(() => {
      followUpTimer = null;
      followUpAt = 0;
      followUp();
    }, timeToFinalFollowUp);
  };
}

// Call after any action that may change the logged-in user's own reservation
// count. Not for concierge actions taken on behalf of another guest.
function markReservationsChangedThunk() {
  return (dispatch, getState) => {
    setReservationsChangedAt(getState().user.userData?.id, Date.now());
  };
}

function registerUserThunk(account, captchaResponse) {
  return (dispatch, getState) => {
    const userObj = { ...account };
    const { user: userData } = getState();
    if (userData?.registrationStatus !== PENDING) {
      return dispatch(postRegisterUserThunk({ userObj, captchaResponse }))
        .then((response) => {
          if (response.error) {
            throw (new Error(`postRegisterUser ERROR: ${response?.payload?.message}`));
          }
          dispatch(doAuthenticationThunk(response?.payload?.user));
          return Promise.resolve(response);
        });
    }
    return Promise.resolve({});
  };
}

function userLogoutThunk() {
  return async (dispatch, getState) => {
    const analyticsPayload = { category: 'Account' };
    const { user: { userData } } = getState();
    if (userData) {
      analyticsPayload.userId = userData.id;
      analyticsPayload.isLoggedIn = false;
      analyticsPayload.email = userData.em_address;
    }
    // Before the await, so a /2/user response or follow-up from this session can't land while
    // the auth destroy call is in flight
    resetUserFetchState();
    await getAuthDestroy();
    clearReservationsChangedAt();
    dispatch(removeAuthToken(analyticsPayload));
    dispatch(resetUserData());
    dispatch(resetAmexAds());
    dispatch(resetExperiments());
    dispatch(fetchExperiments());
  };
}

function requestEmailCodeThunk(params) {
  return (dispatch) => dispatch(postAuthEmailRequestThunk(params))
    .unwrap()
    .then((data) => data);
}

function doAuthenticationThunk(userData) {
  return (dispatch, getState) => {
    const userDataWithAmexCard = { ...userData, amexCard: userHasAmexCard(userData) };
    const { user: { userLocation } } = getState();
    resetUserFetchState();
    dispatch(setAuthToken(userData?.token));
    dispatch(setUserData(userDataWithAmexCard));
    dispatch(fetchExperiments(userData?.id));
    dispatch(getListVenuesByUserThunk());
    if (isValidLatLong(userLocation)) {
      postUserLocation({
        x: userLocation.long,
        y: userLocation.lat,
      });
    }
    // From User service: Call here to replace reservation.load,
    // though it does not seem to do anything as data was never persisted in User service.
    dispatch(setShouldUserReservationsUpdate({}));
  };
}

function authenticateEmailCodeThunk(params) {
  return (dispatch) => dispatch(postAuthEmailVerifyThunk({ ...params }))
    .unwrap()
    .then((userData) => {
      dispatch(doAuthenticationThunk(userData));
      return userData;
    });
}

function authenticatePasswordThunk(params) {
  return (dispatch) => dispatch(postAuthPasswordThunk({ ...params }))
    .unwrap()
    .then((userData) => {
      dispatch(doAuthenticationThunk(userData));
      return userData;
    });
}

function authenticateMobileThunk(params) {
  return (dispatch) => dispatch(postAuthMobileThunk({ ...params }))
    .unwrap()
    .then((data) => {
      if (data.user) {
        dispatch(doAuthenticationThunk(data.user));
      }
      return data;
    });
}

function authenticateChallengeThunk(params) {
  return (dispatch) => dispatch(postAuthChallengeThunk({ ...params }))
    .unwrap()
    .then((userData) => {
      dispatch(doAuthenticationThunk(userData));
      return userData;
    });
}

// Selectors
const selectIsConcierge = (state) => !!state.user.userData?.is_concierge;
const selectIsGlobalDiningAccess = (state) => !!state.user.userData?.is_global_dining_access;
const selectIsCenturionEligible = (state) => !!state.user.userData?.is_centurion_eligible;
const selectIsPlatinumEligible = (state) => !!state.user.userData?.is_platinum_eligible;
const selectIsPlatinumNight = (state) => !!state.user.userData?.is_platinum_night_eligible;
const selectIsNonUS = (state) => !!state.user.userLocation?.isNonUS;
const selectIsGoldCardMember = (state) => !!state.user.userData?.amex_card_types?.find((item) => item.toLowerCase() === 'gold');
const selectResyAdsId = (state) => state.user.userData?.resy_ads_id;

export {
  authenticateChallengeThunk,
  authenticateEmailCodeThunk,
  authenticateMobileThunk,
  authenticatePasswordThunk,
  getReservationToBeModifiedThunk,
  getUserThunk,
  loadUserThunk,
  markReservationsChangedThunk,
  postAuthChallengeThunk,
  postAuthEmailRequestThunk,
  postAuthEmailVerifyThunk,
  postAuthMobileThunk,
  postAuthPasswordThunk,
  postRegisterUserThunk,
  registerUserThunk,
  requestEmailCodeThunk,
  resetRegistration,
  resetReservationToBeModified,
  RESERVATIONS_CHANGE_SETTLE_MS,
  resetShouldUserReservationsUpdate,
  resetUserData,
  resetUserFetchState,
  selectIsConcierge,
  selectIsGlobalDiningAccess,
  selectIsCenturionEligible,
  selectIsPlatinumEligible,
  selectIsPlatinumNight,
  selectIsGoldCardMember,
  selectIsNonUS,
  selectResyAdsId,
  setReservationToBeModified,
  setShouldUserReservationsUpdate,
  setUserData,
  setUpdatedPhoneNumber,
  setUserLocation,
  setUserLocationLatLong,
  userLogoutThunk,
};

export default userSlice.reducer;
